import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AccountBalanceWalletOutlined,
  AssignmentTurnedInOutlined,
  WarningAmberOutlined,
  LocalPharmacyOutlined,
  ReceiptLongOutlined,
  MonetizationOnOutlined,
} from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import ChartCard from "@/components/data-visuals/ChartCard";
import { dataGridCellNoFocus } from "@/utils/adminTableStyles";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const Z_TINT = "#f3eeff";

const fmt = (v) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(Number(v ?? 0));

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

function ZephyrStatCard({ icon: Icon, label, value, sub, color = Z_BLUE }) {
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 2,
        p: 2.5,
        border: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            background: `${color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20, color }} />
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={600}
          fontSize="0.82rem"
        >
          {label}
        </Typography>
      </Box>
      <Typography fontWeight={800} fontSize="1.35rem" sx={{ color: "#1a1a1a" }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary" fontSize="0.77rem">
          {sub}
        </Typography>
      )}
    </Box>
  );
}

const zephyrDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: `${Z_TINT} !important` },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": { outline: "none !important", boxShadow: "none !important" },
    "&:focus-within": { outline: "none !important", boxShadow: "none !important" },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${Z_PURPLE} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${Z_PURPLE}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${Z_PURPLE} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${Z_PURPLE} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${Z_PURPLE} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
  ...dataGridCellNoFocus,
};

const AdminZephyrClaimsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await phappsRequest(API_PATHS.ZEPHYR_ADMIN_CLAIMS_OVERVIEW, "GET");
        setData(res);
        setFetchError(null);
      } catch (e) {
        console.error(e);
        setFetchError(getAdminApiErrorMessage(e, "Failed to load Zephyr Claims data"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const under30 = Number(data?.OutstandingUnder30 ?? 0);
  const over30 = Number(data?.OutstandingOver30 ?? 0);
  const hasPieData = under30 > 0 || over30 > 0;

  const pieData = hasPieData
    ? [
        { x: "Under 30 days", y: under30, color: Z_BLUE },
        { x: "Over 30 days", y: over30, color: "#dc2626" },
      ].filter((d) => d.y > 0)
    : [{ x: "No outstanding invoices", y: 1, color: "#e5e7eb" }];

  const chemistRows = (data?.ChemistTable || []).map((row, i) => ({
    id: row.ChemistID ?? `r-${i}`,
    ...row,
  }));

  const columns = [
    {
      field: "ZephyrChemShortCode",
      headerName: "Short Code",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ fontFamily: "monospace", color: Z_PURPLE }}
        >
          {p.value || "—"}
        </Typography>
      ),
    },
    {
      field: "ChemistName",
      headerName: "Chemist",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "TotalScriptsQty",
      headerName: "Scripts (qty)",
      width: 130,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => Number(value || 0).toLocaleString(),
    },
    {
      field: "TotalScriptsValue",
      headerName: "Total Value",
      width: 150,
      type: "number",
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) => fmt(value),
    },
    {
      field: "LastBatchDate",
      headerName: "Last Batch",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => fmtDate(p.value),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        {/* Page header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={ZephyrIconWhite}
              alt="Zephyr Claims"
              style={{ height: 20, objectFit: "contain" }}
            />
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: Z_PURPLE }}>
            Zephyr Claims Overview
          </Typography>
        </Box>

        {fetchError && <Alert severity="warning">{fetchError}</Alert>}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: Z_PURPLE }}
              />
              <p className="text-gray-600">Loading Zephyr Claims data…</p>
            </div>
          </div>
        ) : (
          <>
            {/* FY summary banner — scripts total + zephyr revenue in one card */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${Z_PURPLE} 0%, #1e0a36 100%)`,
                borderRadius: 2,
                p: 3,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <ReceiptLongOutlined sx={{ fontSize: 18, color: "rgba(255,255,255,0.65)" }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.8rem", fontWeight: 600 }}>
                    Total Scripts Processed (FY)
                  </Typography>
                </Box>
                <Typography fontWeight={800} fontSize="1.75rem" color="white">
                  {fmt(data?.FYScriptsTotal)}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  Sum of all reconciled script values, current financial year
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <MonetizationOnOutlined sx={{ fontSize: 18, color: Z_BLUE }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.8rem", fontWeight: 600 }}>
                    Zephyr Revenue (FY)
                  </Typography>
                </Box>
                <Typography fontWeight={800} fontSize="1.75rem" sx={{ color: Z_BLUE }}>
                  {fmt(data?.FYZephyrRevenue)}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  Sum of Zephyr fees on reconciled scripts, current financial year
                </Typography>
              </Box>
            </Box>

            {/* Pie chart + stat cards row */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {/* Outstanding pie */}
              <Box
                sx={{
                  width: { xs: "100%", lg: 380 },
                  flexShrink: 0,
                  "& > div": { marginTop: "0 !important" },
                }}
              >
                <ChartCard
                  title=""
                  hideMainChart
                  fillParentHeight
                  pieChartHeight={320}
                  data={{}}
                  primaryXAxis={{}}
                  primaryYAxis={{}}
                  borderColor={Z_PURPLE}
                  series={[]}
                  pieData={pieData}
                  pieTitle="Outstanding by Age"
                />
              </Box>

              {/* Stat cards */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  alignContent: "start",
                }}
              >
                <ZephyrStatCard
                  icon={AccountBalanceWalletOutlined}
                  label="Under 30 days outstanding"
                  value={fmt(under30)}
                  sub="Invoiced, unpaid, not written off — under 30 days old"
                  color={Z_BLUE}
                />
                <ZephyrStatCard
                  icon={WarningAmberOutlined}
                  label="Over 30 days outstanding"
                  value={fmt(over30)}
                  sub="Invoiced, unpaid, not written off — 30+ days old"
                  color="#dc2626"
                />
                <ZephyrStatCard
                  icon={AssignmentTurnedInOutlined}
                  label="Ready for reconciliation"
                  value={fmt(data?.ReadyForRecon)}
                  sub="Scripts matched and waiting for the next statement run"
                  color="#16a34a"
                />
                <ZephyrStatCard
                  icon={LocalPharmacyOutlined}
                  label="Zephyr chemists"
                  value={String(chemistRows.length)}
                  sub="Active chemists with a Zephyr short code"
                  color={Z_PURPLE}
                />
              </Box>
            </Box>

            {/* Chemist table */}
            <Card
              variant="outlined"
              sx={{ borderColor: Z_PURPLE, borderRadius: 2, overflow: "hidden" }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={ZephyrIconWhite}
                      alt=""
                      style={{ height: 14, objectFit: "contain" }}
                    />
                  </Box>
                  <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>
                    Chemist Scripts — Current Financial Year
                  </Typography>
                </Box>
                <Divider />
                <div style={{ height: 480, width: "100%", padding: "0 8px 16px" }}>
                  <DataGrid
                    rows={chemistRows}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[10, 20, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 20 } },
                    }}
                    disableRowSelectionOnClick
                    sx={zephyrDataGridSx}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminZephyrClaimsPage;
